import { ready } from './utilities';
import { colors } from './vars';

const CHART_ID = 'stock-picks-performance-chart';
const DATA_ID = 'stock-picks-performance-data';

ready(() => {
  const chartEl = document.getElementById(CHART_ID);

  if (!chartEl) {
    return;
  }

  import('chart.js-legacy').then(({ default: Chart }) => init(Chart, chartEl));
});

function init(Chart, chartEl) {
  const dataEl = document.getElementById(DATA_ID);
  if (!dataEl) {
    return;
  }

  const performance = JSON.parse(dataEl.textContent || '[]');
  if (!performance.length) {
    return;
  }

  const sorted = performance
    .slice()
    .sort((a, b) => a.year - b.year || a.quarter - b.quarter);

  const labels = sorted.map((entry) => `Q${entry.quarter} ${entry.year}`);
  const isPartial = sorted.map((entry) => Boolean(entry.is_partial));
  const stockReturns = sorted.map((entry) => entry.stock_picks_return);
  const sp500Returns = sorted.map((entry) => entry.sp500_return);

  const avgStockReturn = average(stockReturns);
  const avgSp500Return = average(sp500Returns);
  const avgStockLine = labels.map(() => avgStockReturn);
  const avgSp500Line = labels.map(() => avgSp500Return);

  const lastCompleteIndex = isPartial.lastIndexOf(false);
  const hasPartial = isPartial.some(Boolean);

  const completeStock = stockReturns.map((value, index) =>
    isPartial[index] ? null : value
  );
  const completeSp500 = sp500Returns.map((value, index) =>
    isPartial[index] ? null : value
  );

  const partialStock = stockReturns.map((value, index) => {
    if (isPartial[index]) {
      return value;
    }
    if (hasPartial && index === lastCompleteIndex) {
      return value;
    }
    return null;
  });
  const partialSp500 = sp500Returns.map((value, index) => {
    if (isPartial[index]) {
      return value;
    }
    if (hasPartial && index === lastCompleteIndex) {
      return value;
    }
    return null;
  });

  const ctx = chartEl.getContext('2d');

  Chart.defaults.global.defaultFontColor = 'rgba(33, 30, 60, 0.55)';

  new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Stock Picks',
          borderColor: colors.success.hex,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointBackgroundColor: colors.success.hex,
          pointBorderColor: colors.success.hex,
          pointRadius: 3,
          pointHoverRadius: 5,
          data: completeStock,
          fill: false,
          lineTension: 0.15,
        },
        {
          label: 'Stock Picks (partial)',
          borderColor: colors.success.hex,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          pointBackgroundColor: '#ffffff',
          pointBorderColor: colors.success.hex,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          data: partialStock,
          fill: false,
          lineTension: 0.15,
        },
        {
          label: 'S&P 500',
          borderColor: colors.info.hex,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointBackgroundColor: colors.info.hex,
          pointBorderColor: colors.info.hex,
          pointRadius: 3,
          pointHoverRadius: 5,
          data: completeSp500,
          fill: false,
          lineTension: 0.15,
        },
        {
          label: 'S&P 500 (partial)',
          borderColor: colors.info.hex,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          pointBackgroundColor: '#ffffff',
          pointBorderColor: colors.info.hex,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          data: partialSp500,
          fill: false,
          lineTension: 0.15,
        },
        {
          label: `Stock Picks avg (${formatPercent(avgStockReturn)})`,
          borderColor: colors.success.hex,
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [2, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          data: avgStockLine,
          fill: false,
          lineTension: 0,
        },
        {
          label: `S&P 500 avg (${formatPercent(avgSp500Return)})`,
          borderColor: colors.info.hex,
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [2, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          data: avgSp500Line,
          fill: false,
          lineTension: 0,
        },
      ],
    },
    options: {
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          fontSize: 13,
          usePointStyle: true,
          filter: (legendItem) => !legendItem.text.includes('(partial)'),
        },
      },
      tooltips: {
        mode: 'index',
        position: 'nearest',
        intersect: false,
        filter: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const value = dataset.data[tooltipItem.index];
          if (value == null) {
            return false;
          }
          if (dataset.label.includes(' avg (')) {
            return true;
          }
          const partialDataset = dataset.label.includes('(partial)');
          if (partialDataset) {
            return isPartial[tooltipItem.index];
          }
          return !isPartial[tooltipItem.index];
        },
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].index;
            const label = labels[index];
            if (isPartial[index]) {
              return `${label} · Since release (incomplete year)`;
            }
            return `${label} · 1-year return`;
          },
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            if (dataset.label.includes(' avg (')) {
              return dataset.label;
            }
            const name = dataset.label.replace(' (partial)', '');
            return `${name}: ${formatPercent(tooltipItem.yLabel)}`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            ticks: {
              maxTicksLimit: 16,
              maxRotation: 45,
              minRotation: 0,
            },
            gridLines: {
              color: 'rgba(33, 30, 60, 0.06)',
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              callback: (value) => `${value}%`,
            },
            scaleLabel: {
              display: true,
              labelString: 'Return (%)',
            },
            gridLines: {
              color: 'rgba(33, 30, 60, 0.08)',
              zeroLineColor: 'rgba(33, 30, 60, 0.25)',
            },
          },
        ],
      },
    },
  });
}

function average(values) {
  if (!values.length) {
    return 0;
  }
  const sum = values.reduce((total, value) => total + Number(value), 0);
  return sum / values.length;
}

function formatPercent(value) {
  const rounded = Number(value).toFixed(1);
  return `${value > 0 ? '+' : ''}${rounded}%`;
}
